import { icon, refreshIcons } from '../icons';
import { reimportV1, fullFactoryReset } from '@/storage/migrateFromV1';
import { appState } from '../state';
import { getDriveClientId, setDriveClientId } from '@/lib/googleDrive';

export function renderSettings(root: HTMLElement) {
  const driveConfigured = !!getDriveClientId();
  root.innerHTML = `
    <main class="max-w-2xl mx-auto px-4 sm:px-6 py-10 space-y-6">
      <div class="flex items-center gap-3">
        ${icon('settings', 'w-5 h-5 text-slate-500')}
        <h2 class="text-xl font-semibold">Settings</h2>
      </div>

      <section class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 p-5 space-y-3">
        <div class="flex items-center gap-2">
          ${icon('cloud', 'w-4 h-4 text-slate-500')}
          <h3 class="font-semibold">Google Drive</h3>
          ${driveConfigured ? `<span class="px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">Connected</span>` : `<span class="px-2 py-0.5 text-xs rounded-full bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300">Not configured</span>`}
        </div>
        <p class="text-sm text-slate-500 dark:text-slate-400">
          Lets the upload screen open a file picker for your Google Drive.
          Read-only access is requested — the app never writes to or shares your files.
        </p>
        <label class="block text-xs font-medium text-slate-500 dark:text-slate-400">OAuth Client ID</label>
        <div class="flex items-center gap-2">
          <input id="driveClientId" type="text" inputmode="text" autocomplete="off" spellcheck="false"
            value="${escapeHtml(getDriveClientId() || '')}"
            placeholder="1234567890-abc...apps.googleusercontent.com"
            class="flex-1 min-w-0 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono" />
          <button id="driveSaveBtn" class="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">Save</button>
          ${driveConfigured ? `<button id="driveClearBtn" class="px-3 py-2 rounded-lg border border-rose-200 text-rose-700 dark:border-rose-700/50 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/20 text-sm font-semibold">Clear</button>` : ''}
        </div>
        <span id="driveStatus" class="text-xs text-slate-500"></span>
        <details class="text-sm text-slate-600 dark:text-slate-400">
          <summary class="cursor-pointer font-medium">How do I get a Client ID?</summary>
          <ol class="mt-2 list-decimal list-inside space-y-1 text-xs">
            <li>Open <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" class="underline">console.cloud.google.com</a> and create (or pick) a project.</li>
            <li>Enable the <strong>Google Picker API</strong> and the <strong>Google Drive API</strong> for that project.</li>
            <li>Open <em>APIs &amp; Services → OAuth consent screen</em>: pick External, fill in app name + your email, add yourself as a test user.</li>
            <li>Open <em>APIs &amp; Services → Credentials → Create credentials → OAuth client ID</em>: choose <strong>Web application</strong>.</li>
            <li>Under <em>Authorised JavaScript origins</em> add:
              <ul class="list-disc list-inside ml-4 mt-1">
                <li><code>http://localhost:5173</code> (local dev)</li>
                <li><code>https://budget-tracker-fey.pages.dev</code> (production)</li>
              </ul>
            </li>
            <li>Copy the Client ID it gives you and paste it above.</li>
          </ol>
          <p class="mt-2 text-xs text-slate-500">Read-only scope (<code>drive.readonly</code>) is requested — your files are downloaded only when you pick them.</p>
        </details>
      </section>

      <section class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 p-5 space-y-3">
        <h3 class="font-semibold">v1 data</h3>
        <p class="text-sm text-slate-500 dark:text-slate-400">Re-import expenses from the v1 localStorage key (<code>budgetTracker.v1</code>). The original key is never deleted.</p>
        <div class="flex items-center gap-2">
          <button id="reimportBtn" class="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">Re-import v1 data</button>
          <span id="reimportStatus" class="text-xs text-slate-500"></span>
        </div>
      </section>

      <section class="rounded-2xl border border-rose-200 dark:border-rose-700/40 bg-rose-50/40 dark:bg-rose-900/20 p-5 space-y-3">
        <h3 class="font-semibold text-rose-700 dark:text-rose-300">Danger zone</h3>
        <p class="text-sm text-slate-600 dark:text-slate-300">Wipe everything and restore the March 2026 factory seed.</p>
        <button id="factoryResetBtn" class="px-3 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold">Factory reset</button>
      </section>

      <section class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 p-5 space-y-3 text-xs text-slate-500">
        <h3 class="font-semibold text-slate-700 dark:text-slate-200 text-sm">About</h3>
        <p>Budget Tracker v2 · single-file PWA · SQLite-WASM in OPFS · no backend.</p>
        <p>State: schema v1, theme=${appState.get().theme}, currentMonth=${appState.get().currentMonth}</p>
      </section>
    </main>
  `;
  refreshIcons();

  document.getElementById('driveSaveBtn')?.addEventListener('click', () => {
    const v = (document.getElementById('driveClientId') as HTMLInputElement).value.trim();
    if (!v) {
      document.getElementById('driveStatus')!.textContent = 'Please paste a Client ID first.';
      return;
    }
    setDriveClientId(v);
    document.getElementById('driveStatus')!.textContent = 'Saved — open the Upload screen to try it.';
  });
  document.getElementById('driveClearBtn')?.addEventListener('click', () => {
    if (!confirm('Remove the saved Google Drive Client ID?')) return;
    setDriveClientId(null);
    renderSettings(root);
  });

  document.getElementById('reimportBtn')?.addEventListener('click', async () => {
    const status = document.getElementById('reimportStatus')!;
    status.textContent = 'Importing…';
    try {
      const r = await reimportV1();
      status.textContent = `Imported ${r.imported} expense${r.imported === 1 ? '' : 's'} across ${r.months} month${r.months === 1 ? '' : 's'}.`;
    } catch (e) {
      status.textContent = 'Import failed: ' + (e as Error).message;
    }
  });
  document.getElementById('factoryResetBtn')?.addEventListener('click', async () => {
    if (!confirm('Really wipe everything?')) return;
    await fullFactoryReset();
    location.reload();
  });
}

function escapeHtml(s: string) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]!));
}
